import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react'

export type KrakenEntityType = 'item' | 'seller' | 'saw'

export interface KrakenEntity {
  type: KrakenEntityType
  id: string // Raw ID without prefix (for API calls)
  fullId: string // ID with prefix (for display/identification)
  name?: string
}

interface KrakenContextValue {
  currentEntity: KrakenEntity | null
  setItemContext: (itemId: string, name?: string) => void
  setSellerContext: (sellerId: string, name?: string) => void
  setSawContext: (sawId: string, name?: string) => void
  clearContext: () => void
}

const KrakenContext = createContext<KrakenContextValue | null>(null)

export const KrakenProvider = ({ children }: { children: ReactNode }) => {
  const [currentEntity, setCurrentEntity] = useState<KrakenEntity | null>(null)

  const setItemContext = useCallback((itemId: string, name?: string) => {
    const rawId = itemId.startsWith('item_') ? itemId.slice(5) : itemId
    const fullId = itemId.startsWith('item_') ? itemId : `item_${itemId}`
    setCurrentEntity({ type: 'item', id: rawId, fullId, name })
  }, [])

  const setSellerContext = useCallback((sellerId: string, name?: string) => {
    const rawId = sellerId.startsWith('seller_') ? sellerId.slice(7) : sellerId
    const fullId = sellerId.startsWith('seller_') ? sellerId : `seller_${sellerId}`
    setCurrentEntity({ type: 'seller', id: rawId, fullId, name })
  }, [])

  const setSawContext = useCallback((sawId: string, name?: string) => {
    const rawId = sawId.startsWith('saw_') ? sawId.slice(4) : sawId
    const fullId = sawId.startsWith('saw_') ? sawId : `saw_${sawId}`
    setCurrentEntity({ type: 'saw', id: rawId, fullId, name })
  }, [])

  const clearContext = useCallback(() => {
    setCurrentEntity(null)
  }, [])

  const value = useMemo(
    () => ({
      currentEntity,
      setItemContext,
      setSellerContext,
      setSawContext,
      clearContext,
    }),
    [currentEntity, setItemContext, setSellerContext, setSawContext, clearContext],
  )

  return <KrakenContext.Provider value={value}>{children}</KrakenContext.Provider>
}

export const useKrakenContext = () => {
  const context = useContext(KrakenContext)
  if (!context) {
    throw new Error('useKrakenContext must be used within a KrakenProvider')
  }
  return context
}

export const useKrakenContextOptional = () => {
  return useContext(KrakenContext)
}
